import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Rocket,
  Users,
  AlertTriangle,
  Building2,
  Calendar,
  MessageSquare,
  UserPlus,
  TrendingUp,
  Clock,
  Send,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { PLAN_LABELS, ALL_PLANS } from "@/lib/planLabels";

// ─── Types ────────────────────────────────────────────────────────────────────

type Stage =
  | "REGISTERED"
  | "PROFILE_COMPLETE"
  | "FIRST_QUOTE_CREATED"
  | "FIRST_QUOTE_SENT"
  | "FIRST_TENDER_VIEWED"
  | "ACTIVE";

type RecoveryStep = "REGISTERED" | "PROFILE_COMPLETE" | "FIRST_QUOTE_CREATED" | "FIRST_QUOTE_SENT";

const RECOVERY_STEPS: RecoveryStep[] = [
  "REGISTERED",
  "PROFILE_COMPLETE",
  "FIRST_QUOTE_CREATED",
  "FIRST_QUOTE_SENT",
];

const RECOVERY_TEMPLATES: Record<RecoveryStep, { subject: string; preview: string }> = {
  REGISTERED: {
    subject: "Complétez votre profil Constrigo",
    preview:
      "Bonjour,\n\nVotre compte Constrigo est créé mais votre profil n'est pas encore complet. Un profil complet vous permet de recevoir des appels d'offres correspondant à votre activité.\n\nCliquez ici pour compléter votre profil → [lien profil]\n\nÀ bientôt,\nL'équipe Constrigo",
  },
  PROFILE_COMPLETE: {
    subject: "Créez votre premier devis sur Constrigo",
    preview:
      "Bonjour,\n\nVotre profil est complet — bravo !\n\nL'étape suivante est de créer votre premier devis pour commencer à utiliser Constrigo pleinement.\n\nCréer mon premier devis → [lien devis]\n\nÀ bientôt,\nL'équipe Constrigo",
  },
  FIRST_QUOTE_CREATED: {
    subject: "Envoyez votre devis à votre client",
    preview:
      "Bonjour,\n\nVotre devis est créé — il ne reste plus qu'à l'envoyer à votre client !\n\nEnvoyer mon devis → [lien devis]\n\nBesoin d'aide ? Contactez-nous.\n\nÀ bientôt,\nL'équipe Constrigo",
  },
  FIRST_QUOTE_SENT: {
    subject: "Consultez vos appels d'offres",
    preview:
      "Bonjour,\n\nVotre premier devis est envoyé — félicitations !\n\nDécouvrez maintenant les appels d'offres disponibles correspondant à votre activité.\n\nVoir les AO → [lien AO]\n\nÀ bientôt,\nL'équipe Constrigo",
  },
};

type PlanFilter = "ALL" | "DECOUVERTE" | "ARTISAN" | "PRO_BTP" | "ENTERPRISE";

interface CompanyItem {
  id: string;
  name: string;
  email: string;
  plan: string | null;
  createdAt: string | Date;
  lastActivity: string | Date;
  daysSinceActivity: number;
  stuck: boolean;
  stage: Stage;
}
type DayFilter = 7 | 30 | 90;

// ─── Config ───────────────────────────────────────────────────────────────────

const STAGE_CONFIG: Record<
  Stage,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  REGISTERED: {
    label: "Inscrit",
    color: "text-muted-foreground",
    bgColor: "bg-muted/30",
    borderColor: "border-muted",
  },
  PROFILE_COMPLETE: {
    label: "Profil complet",
    color: "text-primary",
    bgColor: "bg-primary/5",
    borderColor: "border-primary/20",
  },
  FIRST_QUOTE_CREATED: {
    label: "1er devis créé",
    color: "text-warning",
    bgColor: "bg-warning/5",
    borderColor: "border-warning/20",
  },
  FIRST_QUOTE_SENT: {
    label: "1er devis envoyé",
    color: "text-pipeline-demo",
    bgColor: "bg-pipeline-demo/5",
    borderColor: "border-pipeline-demo/20",
  },
  FIRST_TENDER_VIEWED: {
    label: "1er AO consulté",
    color: "text-success",
    bgColor: "bg-success/5",
    borderColor: "border-success/20",
  },
  ACTIVE: {
    label: "Actif",
    color: "text-success",
    bgColor: "bg-success/10",
    borderColor: "border-success/30",
  },
};

const STAGES: Stage[] = [
  "REGISTERED",
  "PROFILE_COMPLETE",
  "FIRST_QUOTE_CREATED",
  "FIRST_QUOTE_SENT",
  "FIRST_TENDER_VIEWED",
  "ACTIVE",
];

// ─── OnboardingRecoveryModal ──────────────────────────────────────────────────

function OnboardingRecoveryModal({
  open,
  onClose,
  company,
  step,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  company: CompanyItem;
  step: RecoveryStep;
  onSent: () => void;
}) {
  const template = RECOVERY_TEMPLATES[step];
  const mutation = trpc.admin.companies.sendRecoveryEmail.useMutation({
    onSuccess: () => {
      toast.success(`Email de relance envoyé à ${company.name}`);
      onSent();
      onClose();
    },
    onError: () => toast.error("Erreur lors de l'envoi"),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Relancer {company.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email meta */}
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground w-16 shrink-0">Destinataire</span>
              <span className="font-medium">{company.email}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground w-16 shrink-0">Étape</span>
              <span className="font-medium">{STAGE_CONFIG[step].label}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground w-16 shrink-0">Sujet</span>
              <span className="font-medium">{template.subject}</span>
            </div>
          </div>

          {/* Email preview */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              Aperçu du message
            </div>
            <div className="rounded-lg border bg-white p-3 text-sm whitespace-pre-wrap text-muted-foreground font-mono text-xs leading-relaxed">
              {template.preview}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Annuler
          </Button>
          <Button
            onClick={() => mutation.mutate({ companyId: company.id, step })}
            disabled={mutation.isPending}
          >
            <Send className="h-4 w-4 mr-2" />
            {mutation.isPending ? "Envoi…" : "Envoyer la relance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Company Card ─────────────────────────────────────────────────────────────

function CompanyCard({ company }: { company: CompanyItem }) {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const navigate = useNavigate();
  const isStuck = company.stuck;
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [lastRelaunchDate, setLastRelaunchDate] = useState<Date | null>(null);

  const canRelaunch = canAct && isStuck && RECOVERY_STEPS.includes(company.stage as RecoveryStep);

  return (
    <div
      className={`rounded-lg border p-3 text-sm transition-shadow hover:shadow-sm ${
        isStuck
          ? "border-destructive/30 bg-destructive/5"
          : "border-border bg-white"
      }`}
    >
      {/* Name + stuck badge */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="font-medium text-sm leading-tight truncate">{company.name}</div>
        {isStuck && (
          <AlertTriangle
            aria-label="Bloqué depuis plus de 7 jours"
            className="h-3.5 w-3.5 shrink-0 text-destructive mt-0.5"
          />
        )}
      </div>

      {/* Plan + inscription date */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
        <Building2 aria-hidden="true" className="h-3 w-3 shrink-0" />
        <span className="truncate">{company.email}</span>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
        {company.plan && (
          <Badge variant="outline" className="text-xs py-0 h-4">
            {PLAN_LABELS[company.plan as keyof typeof PLAN_LABELS] ?? company.plan}
          </Badge>
        )}
        <span className="flex items-center gap-1">
          <Calendar aria-hidden="true" className="h-3 w-3" />
          {new Date(company.createdAt).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
          })}
        </span>
      </div>

      {/* Last activity */}
      <div
        className={`flex items-center gap-1 text-xs mb-2 ${
          isStuck ? "text-destructive" : "text-muted-foreground"
        }`}
      >
        <Clock aria-hidden="true" className="h-3 w-3" />
        {isStuck
          ? `Inactif depuis ${company.daysSinceActivity}j`
          : `Actif il y a ${company.daysSinceActivity}j`}
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-1 border-t border-border/50 pt-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              aria-label={`Envoyer un message à ${company.name}`}
              onClick={() => navigate(`/messages?company=${company.id}`)}
            >
              <MessageSquare aria-hidden="true" className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Envoyer un message</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              aria-label={`Créer un lead pour ${company.name}`}
              onClick={() =>
                navigate(
                  `/sales/leads?newLead=1&company=${encodeURIComponent(company.name)}&email=${encodeURIComponent(company.email)}`
                )
              }
            >
              <UserPlus aria-hidden="true" className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Créer un lead</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              aria-label={`Voir la fiche de ${company.name}`}
              onClick={() => navigate(`/companies/${company.id}`)}
            >
              <Building2 aria-hidden="true" className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Voir la fiche</TooltipContent>
        </Tooltip>

        {canRelaunch && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-warning hover:text-warning"
                aria-label={`Relancer ${company.name}`}
                onClick={() => setShowRecoveryModal(true)}
              >
                <Send aria-hidden="true" className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {lastRelaunchDate
                ? `Relancé le ${lastRelaunchDate.toLocaleDateString("fr-FR")}`
                : "Relancer par email"}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Last relaunch tracking */}
      {canRelaunch && lastRelaunchDate && (
        <div className="text-xs text-warning mt-1.5 flex items-center gap-1">
          <Send aria-hidden="true" className="h-2.5 w-2.5" />
          Relancé le {lastRelaunchDate.toLocaleDateString("fr-FR")}
        </div>
      )}

      {/* Recovery modal */}
      {showRecoveryModal && (
        <OnboardingRecoveryModal
          open={showRecoveryModal}
          onClose={() => setShowRecoveryModal(false)}
          company={company}
          step={company.stage as RecoveryStep}
          onSent={() => setLastRelaunchDate(new Date())}
        />
      )}
    </div>
  );
}

// ─── Kanban Column ─────────────────────────────────────────────────────────────

function KanbanColumn({ stage, companies }: { stage: Stage; companies: CompanyItem[] }) {
  const cfg = STAGE_CONFIG[stage];

  return (
    <div
      role="region"
      aria-label={`Étape : ${cfg.label}`}
      className="flex flex-col min-w-[220px] w-56 shrink-0"
    >
      {/* Column header */}
      <div className={`rounded-t-lg px-3 py-2 border ${cfg.bgColor} ${cfg.borderColor}`}>
        <div className="flex items-center justify-between">
          <span className={`font-semibold text-sm ${cfg.color}`}>{cfg.label}</span>
          <span className="bg-white rounded-full px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            {companies.length}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div
        className={`flex-1 p-2 space-y-2 min-h-[200px] rounded-b-lg border-x border-b ${cfg.borderColor} bg-muted/20`}
      >
        {companies.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-8">Aucune entreprise</div>
        )}
        {companies.map((company) => (
          <CompanyCard key={company.id} company={company} />
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function OnboardingPipeline() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [dayFilter, setDayFilter] = useState<DayFilter>(30);
  const [planFilter, setPlanFilter] = useState<PlanFilter>("ALL");

  const { data, isLoading } = trpc.admin.companies.onboardingFunnel.useQuery({
    page: 1,
    limit: 100, // server caps at max 100 (admin/companies.ts onboardingFunnel) — 200 → BAD_REQUEST too_big → infinite "Chargement…"
  });

  if (isLoading || !data) {
    return (
      <AdminLayout>
        <div role="status" className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement…
        </div>
      </AdminLayout>
    );
  }

  // Client-side filters
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - dayFilter);

  const filtered = data.items.filter((c) => {
    const withinPeriod = new Date(c.createdAt) >= cutoff;
    const matchesPlan = planFilter === "ALL" || c.plan === planFilter;
    return withinPeriod && matchesPlan;
  });

  // Group by stage
  const byStage: Record<Stage, CompanyItem[]> = {
    REGISTERED: [],
    PROFILE_COMPLETE: [],
    FIRST_QUOTE_CREATED: [],
    FIRST_QUOTE_SENT: [],
    FIRST_TENDER_VIEWED: [],
    ACTIVE: [],
  };
  for (const c of filtered) {
    byStage[c.stage as Stage]?.push(c);
  }

  const kpis = data.kpis;
  const stuckInView = filtered.filter((c) => c.stuck).length;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Rocket aria-hidden="true" className="h-6 w-6 text-primary" />
            Pipeline d'onboarding
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Suivi d'activation des nouveaux tenants — {filtered.length} entreprise
            {filtered.length > 1 ? "s" : ""} sur {dayFilter}j
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <Select
            value={String(dayFilter)}
            onValueChange={(v) => setDayFilter(Number(v) as DayFilter)}
          >
            <SelectTrigger className="w-[110px]" aria-label="Période d'inscription">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={planFilter}
            onValueChange={(v) => setPlanFilter(v as PlanFilter)}
          >
            <SelectTrigger className="w-[130px]" aria-label="Filtrer par plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les plans</SelectItem>
              <SelectItem value="DECOUVERTE">Découverte</SelectItem>
              <SelectItem value="ARTISAN">Artisan</SelectItem>
              <SelectItem value="PRO_BTP">Pro BTP</SelectItem>
              <SelectItem value="ENTERPRISE">Entreprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Taux d'activation 30j</div>
                <div className="text-2xl font-bold text-success">{kpis.activationRate30d}%</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  des inscrits deviennent actifs
                </div>
              </div>
              <div aria-hidden="true" className="p-2 rounded-lg bg-success/10 text-success">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Bloqués +7j (vue courante)</div>
                <div
                  className={`text-2xl font-bold ${stuckInView > 0 ? "text-destructive" : "text-success"}`}
                  role="status"
                >
                  {stuckInView}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">entreprises sans activité</div>
              </div>
              <div
                aria-hidden="true"
                className={`p-2 rounded-lg ${stuckInView > 0 ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}
              >
                <AlertTriangle className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Total entreprises</div>
                <div className="text-2xl font-bold">{kpis.totalCompanies}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  dont {kpis.stageDistribution.ACTIVE} actives
                </div>
              </div>
              <div aria-hidden="true" className="p-2 rounded-lg bg-muted text-muted-foreground">
                <Users className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((stage) => (
          <KanbanColumn key={stage} stage={stage} companies={byStage[stage]} />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 text-destructive" />
          Bloqué = inactif depuis plus de 7 jours (hors actifs)
        </span>
        <span className="text-muted-foreground/50">·</span>
        <span>Filtrage par date d'inscription</span>
      </div>
    </AdminLayout>
  );
}
